import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Progress,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  BarChart3,
  Briefcase,
  Building2,
  Calendar,
  Compass,
  FileText,
  Gauge,
  HelpCircle,
  Rocket,
  Sparkles,
  UserMinus,
  UserPlus,
  Users,
  Wallet,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getSubscription, getPlans, createCheckoutSession, createPortalSession } from '../../api/billing'
import { PlanInfo, PlanKey } from '../../types/billingTypes'
import ModalTextarea from '../forms/modalForms/modalTextarea'
import handleToast from '../HandleToast'
import { text as textStyles } from '../../themes/styles'

type WizardStep = 0 | 1 | 2

const PLAN_ICONS: Record<string, LucideIcon> = {
  basic: Zap,
  pro: Building2,
  enterprise: Rocket,
}

const FEATURE_ICONS: Record<string, LucideIcon> = {
  workforce: Users,
  classifications: Briefcase,
  explore: Compass,
  ai_assistant: Sparkles,
  scheduling: Calendar,
  faq: HelpCircle,
  analytics: BarChart3,
  recruitment: UserPlus,
  evaluations: Gauge,
  payroll: Wallet,
  documents_signing: FileText,
  outflow: UserMinus,
  time_tracking: Calendar,
  assets: Briefcase,
}

const formatBillingDate = (value: string | null, locale: string) => {
  if (!value) return '—'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

const getSubscriptionStatusLabel = (
  status: string | undefined,
  t: (key: string, defaultValue?: string) => string,
) => {
  if (!status) return t('settings.billing.inactiveStatus', 'No subscription')

  return t(`settings.billing.statuses.${status}`, status)
}

const WIZARD_STEPS = [
  {
    key: 'settings.billing.wizard.information',
    fallback: 'Information',
  },
  {
    key: 'settings.billing.wizard.signature',
    fallback: 'Agreement',
  },
  {
    key: 'settings.billing.wizard.payment',
    fallback: 'Payment',
  },
]

const WizardPlanSummary = ({
  plan,
  t,
}: {
  plan: PlanInfo
  t: (key: string, defaultValue?: string) => string
}) => {
  const PlanIcon = PLAN_ICONS[plan.key] || Zap

  return (
    <Box
      border="1px solid"
      borderColor="solid.50"
      borderRadius="18px"
      p={5}
      bg="white"
      boxShadow="0px 12px 28px rgba(13, 13, 18, 0.04)"
    >
      <Flex justify="space-between" align="flex-start" gap={4} mb={4}>
        <HStack align="flex-start" spacing={3}>
          <Flex
            w="44px"
            h="44px"
            borderRadius="14px"
            bg="primary.0"
            align="center"
            justify="center"
          >
            <Icon as={PlanIcon} w={5} h={5} color="primary.100" />
          </Flex>
          <Box>
            <Text {...textStyles.sectionTitle} mb={1}>
              {plan.name}
            </Text>
            <Text {...textStyles.small}>
              {plan.description}
            </Text>
          </Box>
        </HStack>

        <Box textAlign="right">
          <Text {...textStyles.tiny} mb={1}>
            {t('settings.billing.pricingLabel', 'Monthly pricing')}
          </Text>
          <Flex align="baseline" gap="4px" justify="flex-end">
            <Text {...textStyles.pageTitle}>€{plan.price_per_seat}</Text>
            <Text {...textStyles.muted}>{t('settings.billing.perSeat')}</Text>
          </Flex>
        </Box>
      </Flex>

      <Text {...textStyles.heading} mb={3}>
        {t('settings.billing.includedFeatures', 'Included features')}
      </Text>

      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3}>
        {plan.features.map((feature, index) => {
          const FeatureIcon = FEATURE_ICONS[feature] || Zap

          return (
            <Flex
              key={`${feature}-${index}`}
              gap={3}
              align="flex-start"
              border="1px solid"
              borderColor="solid.50"
              borderRadius="14px"
              bg="solid.25"
              p={3}
            >
              <Flex
                w="32px"
                h="32px"
                minW="32px"
                borderRadius="10px"
                bg="white"
                border="1px solid"
                borderColor="solid.50"
                align="center"
                justify="center"
              >
                <Icon as={FeatureIcon} w={4} h={4} color="solid.700" />
              </Flex>

              <Box minW={0}>
                <Text {...textStyles.label}>
                  {t(`settings.billing.features.${feature}`, feature)}
                </Text>
                <Text {...textStyles.small}>
                  {t(`settings.billing.featureDescriptions.${feature}`, '')}
                </Text>
              </Box>
            </Flex>
          )
        })}
      </Grid>
    </Box>
  )
}

const WizardProgress = ({
  step,
  t,
}: {
  step: WizardStep
  t: (key: string, defaultValue?: string) => string
}) => {
  const progressValue = ((step + 1) / WIZARD_STEPS.length) * 100

  return (
    <VStack align="stretch" gap={4} mb={6}>
      <Progress
        value={progressValue}
        size="sm"
        borderRadius="999px"
        bg="white"
        border="1px solid"
        borderColor="solid.50"
        sx={{
          '& > div': {
            background: 'linear-gradient(90deg, var(--chakra-colors-warning-100) 0%, var(--chakra-colors-primary-100) 52%, var(--chakra-colors-aqua-100) 100%)',
            borderRadius: '999px',
          },
        }}
      />
      <HStack justify="space-between" align="flex-start">
        {WIZARD_STEPS.map((stepLabel, index) => {
          const isActive = index === step
          const isDone = index < step

          return (
            <VStack key={stepLabel.key} flex={1} gap={2}>
              <Flex
                w="28px"
                h="28px"
                borderRadius="full"
                align="center"
                justify="center"
                border="1px solid"
                borderColor={isActive || isDone ? 'primary.100' : 'solid.100'}
                bg={isActive || isDone ? 'primary.0' : 'white'}
              >
                <Text
                  {...textStyles.tiny}
                  color={isActive || isDone ? 'primary.100' : 'solid.400'}
                >
                  {index + 1}
                </Text>
              </Flex>
              <Text
                textAlign="center"
                {...textStyles.tiny}
                color={isActive ? 'solid.900' : 'solid.400'}
              >
                {t(stepLabel.key, stepLabel.fallback)}
              </Text>
            </VStack>
          )
        })}
      </HStack>
    </VStack>
  )
}

const SubscriptionWizard = ({
  plan,
  isOpen,
  onClose,
  onSubmit,
  t,
  isLoading,
}: {
  plan: PlanInfo | null
  isOpen: boolean
  onClose: () => void
  onSubmit: (planKey: PlanKey) => Promise<void>
  t: (key: string, defaultValue?: string) => string
  isLoading: boolean
}) => {
  const [step, setStep] = useState<WizardStep>(0)
  const [signature, setSignature] = useState('')

  useEffect(() => {
    if (!isOpen) {
      setStep(0)
      setSignature('')
    }
  }, [isOpen])

  const canContinue = useMemo(() => {
    if (step === 1) {
      return signature.trim().length > 0
    }
    return true
  }, [step, signature])

  if (!plan) return null

  const goNext = () => {
    if (step < 2) {
      setStep((prev) => (prev + 1) as WizardStep)
    }
  }

  const goBack = () => {
    if (step > 0) {
      setStep((prev) => (prev - 1) as WizardStep)
    } else {
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6px)" />
      <ModalContent borderRadius="24px" overflow="hidden" bg="solid.0">
        <ModalBody p={0}>
          <Box
            px={{ base: 5, md: 6 }}
            py={{ base: 5, md: 6 }}
            bgGradient="linear(to-r, primary.0, aqua.0, solid.0)"
            borderBottom="1px solid"
            borderColor="solid.50"
          >
            <Grid templateColumns={{ base: '1fr', lg: '1.4fr 0.8fr' }} gap={5} alignItems="end">
              <Box>
                <Text {...textStyles.tiny} color="primary.100" mb={2}>
                  {t('settings.billing.wizard.eyebrow', 'Subscription setup')}
                </Text>
                <Text {...textStyles.pageTitle} mb={2}>
                  {t('settings.billing.wizard.title', 'Set up your organization plan')}
                </Text>
                <Text {...textStyles.body}>
                  {t('settings.billing.wizard.subtitle', 'Review the plan, add an approval note, and continue to Stripe when everything looks right.')}
                </Text>
              </Box>

              <Box
                bg="white"
                border="1px solid"
                borderColor="solid.50"
                borderRadius="18px"
                p={4}
              >
                <Text {...textStyles.tiny} mb={2}>
                  {t('settings.billing.wizard.selectedPlanLabel', 'Selected plan')}
                </Text>
                <Text {...textStyles.heading} mb={1}>
                  {plan.name}
                </Text>
                <Text {...textStyles.small}>
                  €{plan.price_per_seat} {t('settings.billing.perSeat')}
                </Text>
              </Box>
            </Grid>
          </Box>

          <Box p={{ base: 5, md: 6 }}>
          <WizardProgress step={step} t={t} />

          {step === 0 && (
            <VStack align="stretch" gap={5}>
              <Box>
                <Text {...textStyles.sectionTitle} mb={2}>
                  {t('settings.billing.wizard.informationTitle', 'Review plan details')}
                </Text>
                <Text {...textStyles.muted}>
                  {t('settings.billing.wizard.informationSubtitle', 'Confirm the plan and included features before continuing to the billing step.')}
                </Text>
              </Box>

              <WizardPlanSummary plan={plan} t={t} />
            </VStack>
          )}

          {step === 1 && (
            <Grid templateColumns={{ base: '1fr', lg: '1.1fr 0.9fr' }} gap={5}>
              <VStack align="stretch" gap={5}>
                <Box>
                  <Text {...textStyles.sectionTitle} mb={2}>
                    {t('settings.billing.wizard.signatureTitle', 'Confirm billing approval')}
                  </Text>
                  <Text {...textStyles.muted}>
                    {t('settings.billing.wizard.signatureSubtitle', 'Add the approval details for your records before opening Stripe Checkout.')}
                  </Text>
                </Box>

                <Box
                  border="1px solid"
                  borderColor="solid.50"
                  borderRadius="18px"
                  p={5}
                  bg="white"
                >
                  <ModalTextarea
                    label={t('settings.billing.wizard.signatureInputLabel', 'Approval note')}
                    name="signature"
                    placeholder={t('settings.billing.wizard.signaturePlaceholder', 'Example: Approved by Jane Doe on behalf of AboutWrk.')}
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                    minH="180px"
                    resize="vertical"
                    required
                  />
                </Box>
              </VStack>

              <Box
                border="1px solid"
                borderColor="solid.50"
                borderRadius="18px"
                p={5}
                bg="solid.25"
              >
                <Text {...textStyles.heading} mb={2}>
                  {t('settings.billing.wizard.approvalChecklistTitle', 'Before you continue')}
                </Text>
                <VStack align="stretch" gap={3}>
                  <Box>
                    <Text {...textStyles.label}>
                      {t('settings.billing.wizard.approvalChecklistOneTitle', 'Capture who approved the purchase')}
                    </Text>
                    <Text {...textStyles.small}>
                      {t('settings.billing.wizard.approvalChecklistOneBody', 'Use the note to record the approver, date, or any internal reference you need later.')}
                    </Text>
                  </Box>
                  <Box>
                    <Text {...textStyles.label}>
                      {t('settings.billing.wizard.approvalChecklistTwoTitle', 'Confirm the selected plan')}
                    </Text>
                    <Text {...textStyles.small}>
                      {t('settings.billing.wizard.approvalChecklistTwoBody', 'Stripe Checkout will open for the plan you selected in the pricing overview.')}
                    </Text>
                  </Box>
                  <Box>
                    <Text {...textStyles.label}>
                      {t('settings.billing.wizard.approvalChecklistThreeTitle', 'Keep billing ownership clear')}
                    </Text>
                    <Text {...textStyles.small}>
                      {t('settings.billing.wizard.approvalChecklistThreeBody', 'This makes renewals, upgrades, and finance follow-up easier for your team.')}
                    </Text>
                  </Box>
                </VStack>
              </Box>
            </Grid>
          )}

          {step === 2 && (
            <Grid templateColumns={{ base: '1fr', lg: '1fr 0.9fr' }} gap={5}>
              <VStack align="stretch" gap={5}>
                <Box>
                  <Text {...textStyles.sectionTitle} mb={2}>
                    {t('settings.billing.wizard.paymentTitle', 'Complete payment in Stripe')}
                  </Text>
                  <Text {...textStyles.muted}>
                    {t('settings.billing.wizard.paymentSubtitle', 'You will be redirected to Stripe Checkout to add a payment method and finish the subscription.')}
                  </Text>
                </Box>

                <Box
                  border="1px solid"
                  borderColor="solid.50"
                  borderRadius="18px"
                  p={5}
                  bg="white"
                >
                  <Flex justify="space-between" align="center" mb={3}>
                    <Box>
                      <Text {...textStyles.sectionTitle}>
                        {plan.name}
                      </Text>
                      <Text {...textStyles.small}>
                        {plan.description}
                      </Text>
                    </Box>
                    <Flex align="baseline" gap="4px">
                      <Text {...textStyles.pageTitle}>
                        €{plan.price_per_seat}
                      </Text>
                      <Text {...textStyles.muted}>
                        {t('settings.billing.perSeat')}
                      </Text>
                    </Flex>
                  </Flex>

                  <Box
                    borderTop="1px solid"
                    borderColor="solid.50"
                    pt={3}
                  >
                    <Text {...textStyles.heading} mb={1}>
                      {t('settings.billing.wizard.paymentMethodLabel', 'Payment method')}
                    </Text>
                    <Text {...textStyles.small}>
                      {t('settings.billing.wizard.paymentMethodValue', 'Card details are collected securely by Stripe Checkout.')}
                    </Text>
                  </Box>
                </Box>
              </VStack>

              <Box
                border="1px solid"
                borderColor="solid.50"
                borderRadius="18px"
                p={5}
                bg="solid.25"
              >
                <Text {...textStyles.heading} mb={3}>
                  {t('settings.billing.wizard.checkoutExpectTitle', 'What happens next')}
                </Text>
                <VStack align="stretch" gap={3}>
                  <Box>
                    <Text {...textStyles.label}>
                      {t('settings.billing.wizard.checkoutExpectOneTitle', 'You will leave AboutWrk briefly')}
                    </Text>
                    <Text {...textStyles.small}>
                      {t('settings.billing.wizard.checkoutExpectOneBody', 'Stripe opens in a secure hosted checkout so payment details are never handled directly in the app.')}
                    </Text>
                  </Box>
                  <Box>
                    <Text {...textStyles.label}>
                      {t('settings.billing.wizard.checkoutExpectTwoTitle', 'Your plan syncs back automatically')}
                    </Text>
                    <Text {...textStyles.small}>
                      {t('settings.billing.wizard.checkoutExpectTwoBody', 'After payment, Stripe redirects back and the subscription status updates from the webhook flow.')}
                    </Text>
                  </Box>
                  <Box>
                    <Text {...textStyles.label}>
                      {t('settings.billing.wizard.checkoutExpectThreeTitle', 'You can manage billing later')}
                    </Text>
                    <Text {...textStyles.small}>
                      {t('settings.billing.wizard.checkoutExpectThreeBody', 'Once active, the billing portal lets you update payment methods or manage the subscription.')}
                    </Text>
                  </Box>
                </VStack>
              </Box>
            </Grid>
          )}

          <Flex justify="space-between" mt={8} pt={5} borderTop="1px solid" borderColor="solid.50">
            <Button
              variant="white"
              h="42px"
              borderRadius="12px"
              onClick={goBack}
            >
              {step === 0
                ? t('common.actions.cancel')
                : t('common.actions.back')}
            </Button>

            {step < 2 ? (
              <Button
                variant="mono"
                h="42px"
                borderRadius="12px"
                onClick={goNext}
                isDisabled={!canContinue}
              >
                {t('common.actions.next')}
              </Button>
            ) : (
              <Button
                variant="mono"
                h="42px"
                borderRadius="12px"
                onClick={() => onSubmit(plan.key as PlanKey)}
                isLoading={isLoading}
              >
                {t('settings.billing.wizard.continueToPayment', 'Continue to payment')}
              </Button>
            )}
          </Flex>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const PlanCard = ({
  plan,
  isCurrent,
  isActive,
  currentPlanIndex,
  planIndex,
  onSubscribe,
  onManage,
  t,
}: {
  plan: PlanInfo
  isCurrent: boolean
  isActive: boolean
  currentPlanIndex: number
  planIndex: number
  onSubscribe: (key: PlanKey) => void
  onManage: () => void
  t: (key: string, defaultValue?: string) => string
}) => {
  const PlanIcon = PLAN_ICONS[plan.key] || Zap
  const [isExpanded, setIsExpanded] = useState(false)

  const visibleFeatures = isExpanded ? plan.features : plan.features.slice(0, 6)
  const hasMoreFeatures = plan.features.length > 6

  const accent = isCurrent
    ? {
        iconBg: 'primary.100',
        iconColor: 'white',
        badgeBg: 'primary.0',
        badgeColor: 'primary.100',
        borderColor: 'primary.50',
        shadow: '0px 18px 40px rgba(93, 94, 203, 0.14)',
      }
    : plan.key === 'enterprise'
      ? {
          iconBg: 'white',
          iconColor: 'warning.200',
          badgeBg: 'white',
          badgeColor: 'warning.200',
          borderColor: 'warning.25',
          shadow: '0px 18px 40px rgba(255, 190, 76, 0.14)',
        }
      : plan.key === 'pro'
        ? {
            iconBg: 'aqua.0',
            iconColor: 'aqua.200',
            badgeBg: 'aqua.0',
            badgeColor: 'aqua.200',
            borderColor: 'aqua.25',
            shadow: '0px 18px 40px rgba(51, 207, 255, 0.12)',
          }
        : {
            iconBg: 'primary.0',
            iconColor: 'primary.100',
            badgeBg: 'primary.0',
            badgeColor: 'primary.100',
            borderColor: 'transparent',
            shadow: '0px 16px 36px rgba(93, 94, 203, 0.12)',
          }

  const topBadge = isCurrent
    ? t('settings.billing.currentPlan')
    : plan.key === 'enterprise'
      ? t('settings.billing.recommended', 'Best for scaling teams')
      : plan.key === 'pro'
        ? t('settings.billing.popular', 'Most popular')
        : t('settings.billing.starter', 'Great place to start')

  return (
    <Box
      bgGradient={
        plan.key === 'enterprise'
          ? 'linear(to-b, warning.25, warning.0, solid.0)'
          : plan.key === 'pro'
            ? 'linear(to-b, aqua.25, aqua.0, solid.0)'
            : 'linear(to-b, primary.25, primary.0, solid.0)'
      }
      borderRadius="20px"
      border="1px solid"
      borderColor={accent.borderColor}
      p={6}
      height="100%"
      display="flex"
      flexDirection="column"
      position="relative"
      overflow="hidden"
      boxShadow={accent.shadow}
    >
      <VStack align="stretch" gap={6} position="relative" zIndex={1} flex={1}>
        <Flex justify="space-between" align="flex-start">
          <VStack align="flex-start" gap={3}>
            <Flex
              w="48px"
              h="48px"
              borderRadius="14px"
              bg={accent.iconBg}
              align="center"
              justify="center"
            >
              <Icon as={PlanIcon} w={5} h={5} color={accent.iconColor} />
            </Flex>

            <Badge
              bg={accent.badgeBg}
              color={accent.badgeColor}
              borderRadius="999px"
              px={3}
              py={1}
              textTransform="none"
            >
              <Text {...textStyles.tiny} color="inherit">
                {topBadge}
              </Text>
            </Badge>
          </VStack>

          {isActive && !isCurrent ? (
            <Button
              variant="mono"
              size="sm"
              h="34px"
              px={4}
              borderRadius="10px"
              onClick={() => onSubscribe(plan.key as PlanKey)}
            >
              {planIndex > currentPlanIndex
                ? t('settings.billing.upgrade')
                : t('settings.billing.downgrade')}
            </Button>
          ) : null}
        </Flex>

        <Box>
          <Text {...textStyles.sectionTitle} mb={2}>
            {plan.name}
          </Text>
          <Text {...textStyles.body}>
            {plan.description}
          </Text>
        </Box>

        <Box
          bg="white"
          borderRadius="16px"
          border="1px solid"
          borderColor="solid.50"
          p={4}
        >
          <Text {...textStyles.tiny} mb={2}>
            {t('settings.billing.pricingLabel', 'Monthly pricing')}
          </Text>
          <Flex align="baseline" gap="4px">
            <Text {...textStyles.pageTitle}>€{plan.price_per_seat}</Text>
            <Text {...textStyles.muted}>{t('settings.billing.perSeat')}</Text>
          </Flex>
        </Box>

        <Box
          bg="solid.25"
          borderRadius="16px"
          border="1px solid"
          borderColor="solid.50"
          p={4}
          flex={1}
        >
          <Text {...textStyles.heading} mb={4}>
            {t('settings.billing.includedFeatures', 'Included features')}
          </Text>

          <VStack align="stretch" gap={3}>
            {visibleFeatures.map((feature, i) => {
              const FeatureIcon = FEATURE_ICONS[feature] || Zap

              return (
                <Flex key={`${feature}-${i}`} gap={3} align="flex-start">
                  <Flex
                    w="32px"
                    h="32px"
                    minW="32px"
                    borderRadius="10px"
                    bg="white"
                    border="1px solid"
                    borderColor="solid.50"
                    align="center"
                    justify="center"
                  >
                    <Icon as={FeatureIcon} w={4} h={4} color="solid.700" />
                  </Flex>
                  <Box flex={1} minW={0} pt="4px">
                    <Text {...textStyles.label}>
                      {t(`settings.billing.features.${feature}`, feature)}
                    </Text>
                    <Text {...textStyles.small}>
                      {t(`settings.billing.featureDescriptions.${feature}`, '')}
                    </Text>
                  </Box>
                </Flex>
              )
            })}
          </VStack>

          {hasMoreFeatures ? (
            <Button
              variant="ghost"
              px={0}
              mt={4}
              justifyContent="flex-start"
              onClick={() => setIsExpanded((prev) => !prev)}
              _hover={{ bg: 'transparent', color: 'primary.100' }}
            >
              <Text {...textStyles.label} color="primary.100">
                {isExpanded
                  ? t('settings.billing.showLess', 'Show fewer features')
                  : t('settings.billing.showMore', 'Show all features')}
              </Text>
            </Button>
          ) : null}
        </Box>

        <Box mt="auto">
          {!isActive ? (
            <Button
              variant="mono"
              w="100%"
              h="46px"
              borderRadius="12px"
              onClick={() => onSubscribe(plan.key as PlanKey)}
            >
              {t('settings.billing.subscribe')}
            </Button>
          ) : null}

          {isCurrent ? (
            <Button
              variant="white"
              w="100%"
              h="46px"
              borderRadius="12px"
              color="solid.500"
              onClick={onManage}
            >
              {t('settings.billing.cancelPlan')}
            </Button>
          ) : null}
        </Box>
      </VStack>
    </Box>
  )
}

const PaymentsSettings = () => {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [selectedPlan, setSelectedPlan] = useState<PlanInfo | null>(null)
  const [isRedirectingToStripe, setIsRedirectingToStripe] = useState(false)

  const { data: subscription, refetch } = useQuery({
    queryKey: ['subscription'],
    queryFn: getSubscription,
  })

  const { data: plans } = useQuery({
    queryKey: ['billing-plans'],
    queryFn: getPlans,
    staleTime: 60 * 1000,
  })

  const orderedPlans = useMemo(() => {
    if (!plans) return []

    const preferredOrder: PlanKey[] = ['basic', 'pro', 'enterprise']
    return [...plans].sort(
      (a, b) => preferredOrder.indexOf(a.key as PlanKey) - preferredOrder.indexOf(b.key as PlanKey),
    )
  }, [plans])

  useEffect(() => {
    const success = searchParams.get('success')
    const canceled = searchParams.get('canceled')

    if (success === 'true') {
      handleToast(
        toast,
        true,
        t('settings.billing.subscriptionSuccessful'),
        t('settings.billing.planNowActive'),
      )
      refetch()
    } else if (canceled === 'true') {
      handleToast(
        toast,
        false,
        t('settings.billing.checkoutCanceled'),
        t('settings.billing.canSubscribeAnytime'),
      )
    }

    if (success || canceled) {
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams, toast, refetch, t])

  const handleSubscribe = (planKey: PlanKey) => {
    const plan = orderedPlans.find((item) => item.key === planKey)
    if (!plan) return

    setSelectedPlan(plan)
    onOpen()
  }

  const handleConfirmSubscription = async (planKey: PlanKey) => {
    try {
      setIsRedirectingToStripe(true)
      const result = await createCheckoutSession(planKey)
      if (result.upgraded) {
        setIsRedirectingToStripe(false)
        onClose()
        handleToast(toast, true, t('settings.billing.subscriptionSuccessful'), t('settings.billing.planUpdated', 'Plan updated successfully'))
        refetch()
      } else if (result.url) {
        window.location.href = result.url
      }
    } catch (error: any) {
      setIsRedirectingToStripe(false)
      const rawData = error?.response?.data
      const message =
        typeof rawData === 'string' && rawData.trim()
          ? rawData
          : rawData?.detail
            ? rawData.detail
            : `Checkout failed${error?.response?.status ? ` (${error.response.status})` : ''}`

      handleToast(toast, false, t('common.states.error'), message)
    }
  }

  const handleManageSubscription = async () => {
    try {
      const portalUrl = await createPortalSession()
      window.location.href = portalUrl
    } catch {
      handleToast(
        toast,
        false,
        t('common.states.error'),
        t('settings.billing.portalError'),
      )
    }
  }

  const isActive = !!subscription?.hasSubscription
  const currentPlanIndex = orderedPlans.findIndex((p) => p.key === subscription?.plan)
  const currentPlan = orderedPlans.find((plan) => plan.key === subscription?.plan)
  const renewalDate = formatBillingDate(subscription?.currentPeriodEnd || null, i18n.language)
  const subscriptionStatusLabel = subscription?.cancelAtPeriodEnd
    ? t('settings.billing.cancelScheduled', 'Ends at period end')
    : isActive
      ? t('settings.billing.activeStatus', 'Active')
      : t('settings.billing.inactiveStatus', 'No subscription')

  if (orderedPlans.length === 0) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Text {...textStyles.muted}>
          {t('settings.billing.noPlansAvailable', 'No plans available')}
        </Text>
      </Flex>
    )
  }

  return (
    <>
      <VStack gap={6} width="100%" align="stretch">
        <Box
          borderRadius="24px"
          overflow="hidden"
          border="1px solid"
          borderColor="solid.50"
          bg="solid.0"
          boxShadow="0px 24px 60px rgba(13, 13, 18, 0.06)"
        >
          <Box
            bgGradient="linear(to-r, warning.0, primary.0, aqua.0)"
            px={{ base: 5, md: 7 }}
            py={{ base: 6, md: 7 }}
          >
            <Grid templateColumns={{ base: '1fr', lg: '1.5fr 1fr' }} gap={5} alignItems="end">
              <Box>
                <Text {...textStyles.tiny} color="primary.100" mb={2}>
                  {t('settings.billing.eyebrow', 'Flexible billing for growing teams')}
                </Text>
                <Text {...textStyles.pageTitle} mb={3}>
                  {t('settings.billing.title', 'Pick the plan that fits how your organization works')}
                </Text>
                <Text {...textStyles.body}>
                  {t('settings.billing.subtitle', 'Compare features, review what is included, and move to Stripe checkout when you are ready to activate or change your subscription.')}
                </Text>
              </Box>

              <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={3}>
                <Box bg="white" borderRadius="16px" border="1px solid" borderColor="solid.50" p={4}>
                  <Text {...textStyles.tiny} mb={2}>
                    {t('settings.billing.activePlanLabel', 'Current selection')}
                  </Text>
                  <Text {...textStyles.heading}>
                    {isActive
                      ? currentPlan?.name || t('settings.billing.currentPlan')
                      : t('settings.billing.noActivePlan', 'No active plan')}
                  </Text>
                </Box>
                <Box bg="white" borderRadius="16px" border="1px solid" borderColor="solid.50" p={4}>
                  <Text {...textStyles.tiny} mb={2}>
                    {t('settings.billing.seatsLabel', 'Current seats')}
                  </Text>
                  <Text {...textStyles.heading}>
                    {subscription?.seats || 0}
                  </Text>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box px={{ base: 4, md: 6 }} py={{ base: 5, md: 6 }}>
            <Grid templateColumns={{ base: '1fr', lg: '1.3fr 1fr' }} gap={5} mb={5}>
              <Box
                bg="white"
                borderRadius="18px"
                border="1px solid"
                borderColor="solid.50"
                p={5}
                boxShadow="0px 12px 28px rgba(13, 13, 18, 0.04)"
              >
                <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={4} direction={{ base: 'column', md: 'row' }}>
                  <Box>
                    <Text {...textStyles.tiny} mb={2}>
                      {t('settings.billing.summaryTitle', 'Billing overview')}
                    </Text>
                    <Text {...textStyles.sectionTitle} mb={2}>
                      {currentPlan?.name || t('settings.billing.noActivePlan', 'No active plan')}
                    </Text>
                    <Text {...textStyles.body}>
                      {isActive
                        ? t('settings.billing.summaryActive', 'Your organization is set up for billing and can manage the subscription in Stripe.')
                        : t('settings.billing.summaryInactive', 'Choose a plan below to start billing and unlock subscription-based modules.')}
                    </Text>
                  </Box>

                  {isActive ? (
                    <Button
                      variant="white"
                      h="42px"
                      borderRadius="12px"
                      onClick={handleManageSubscription}
                    >
                      {t('settings.billing.manageBilling', 'Manage billing')}
                    </Button>
                  ) : null}
                </Flex>
              </Box>

              <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={3}>
                <Box bg="white" borderRadius="18px" border="1px solid" borderColor="solid.50" p={4}>
                  <Text {...textStyles.tiny} mb={2}>
                    {t('settings.billing.statusLabel', 'Billing status')}
                  </Text>
                  <Text {...textStyles.heading} mb={1}>
                    {subscriptionStatusLabel}
                  </Text>
                  <Text {...textStyles.small}>
                    {getSubscriptionStatusLabel(subscription?.status, t)}
                  </Text>
                </Box>
                <Box bg="white" borderRadius="18px" border="1px solid" borderColor="solid.50" p={4}>
                  <Text {...textStyles.tiny} mb={2}>
                    {t('settings.billing.renewalLabel', 'Renewal date')}
                  </Text>
                  <Text {...textStyles.heading} mb={1}>
                    {renewalDate}
                  </Text>
                  <Text {...textStyles.small}>
                    {t('settings.billing.renewalHint', 'Updates automatically from Stripe events')}
                  </Text>
                </Box>
              </Grid>
            </Grid>

            <Grid templateColumns={{ base: '1fr', xl: 'repeat(3, minmax(0, 1fr))' }} gap={5}>
              {orderedPlans.map((plan, index) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isCurrent={isActive && subscription?.plan === plan.key}
                  isActive={isActive}
                  currentPlanIndex={currentPlanIndex}
                  planIndex={index}
                  onSubscribe={handleSubscribe}
                  onManage={handleManageSubscription}
                  t={t}
                />
              ))}
            </Grid>
          </Box>
        </Box>
      </VStack>

      <SubscriptionWizard
        plan={selectedPlan}
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleConfirmSubscription}
        t={t}
        isLoading={isRedirectingToStripe}
      />
    </>
  )
}

export default PaymentsSettings
